import React, { useMemo } from 'react';
import { Box, Grid, MenuItem, Paper, Select, Stack, Tooltip, Typography } from '@mui/material';
import { RISK_TIERS } from './riskTiers';
import { riskInsights } from './riskInsights';
import { formatPoints } from './riskFormat';

const Card = ({ title, children, action }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}
      >
        {title}
      </Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const RADIUS_OPTIONS = [5, 10, 15, 25];

const RiskInsightCards = ({ rows, areaName, pairs, proximityMiles, onProximityMiles, onSelect }) => {
  const insight = useMemo(() => riskInsights(rows), [rows]);
  const pct = insight.elevatedPct === null ? null : Math.round(insight.elevatedPct * 100);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <Card title="Regional risk">
          {insight.total ? (
            <>
              <Typography variant="h5" fontWeight={700}>
                {pct}%
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.25 }}>
                of charter schools in {areaName} ({insight.elevatedCount} of {insight.total}) have Moderate or
                higher enrollment risk.
              </Typography>
              <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mb: 0.75 }}>
                {RISK_TIERS.map((tier) =>
                  insight.tierCounts[tier.id] ? (
                    <Tooltip key={tier.id} title={`${tier.label}: ${insight.tierCounts[tier.id]}`}>
                      <Box sx={{ flex: insight.tierCounts[tier.id], bgcolor: tier.color }} />
                    </Tooltip>
                  ) : null
                )}
              </Box>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                {RISK_TIERS.map((tier) => (
                  <Stack key={tier.id} direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tier.color }} />
                    <Typography variant="caption">
                      {tier.label} {insight.tierCounts[tier.id]}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No charter schools match these filters.
            </Typography>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card title="Most common risk drivers">
          {insight.drivers.length ? (
            insight.drivers.map((driver) => (
              <Typography key={driver.id} variant="body2" sx={{ mb: 0.5 }}>
                <b>{driver.label}</b> — top driver for {driver.count} elevated-risk{' '}
                {driver.count === 1 ? 'school' : 'schools'}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No schools at Moderate or higher risk.
            </Typography>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card
          title="Proximity opportunities"
          action={
            <Select
              size="small"
              value={proximityMiles}
              onChange={(event) => onProximityMiles(Number(event.target.value))}
              sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.25 } }}
              inputProps={{ 'aria-label': 'Proximity radius in miles' }}
            >
              {RADIUS_OPTIONS.map((miles) => (
                <MenuItem key={miles} value={miles}>
                  {miles} mi
                </MenuItem>
              ))}
            </Select>
          }
        >
          {pairs.length ? (
            <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {pairs.map((pair) => (
                <Box
                  key={pair.row.school.id}
                  onClick={() => onSelect(pair.row)}
                  sx={{ mb: 0.75, cursor: 'pointer', '&:hover .name': { textDecoration: 'underline' } }}
                >
                  <Typography variant="body2" className="name" fontWeight={600} sx={{ color: pair.row.tier.color }}>
                    {pair.row.school.name} ({pair.row.tier.label}, {formatPoints(pair.row.points)})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {pair.nearestCharter
                      ? `Low-risk charter ${pair.nearestCharter.miles.toFixed(1)} mi: ${pair.nearestCharter.row.school.name}`
                      : 'No low-risk charter in range'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {pair.nearestDistrict
                      ? `Low-risk district school ${pair.nearestDistrict.miles.toFixed(1)} mi: ${pair.nearestDistrict.row.school.name}`
                      : 'No low-risk district peer in range'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No High or Very High risk charter schools in this view.
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default RiskInsightCards;
